import { useState, type MouseEvent } from 'react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@predoc/cow';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  EFAX_STATUS_FILTER_OPTIONS,
  type EfaxStatusFilterValue,
} from '../types';

interface EfaxStatusSelectorProps {
  value: EfaxStatusFilterValue;
  onChange: (status: EfaxStatusFilterValue) => void;
}

export const EfaxStatusSelector = ({
  value,
  onChange,
}: EfaxStatusSelectorProps) => {
  const [open, setOpen] = useState(false);
  const hasActiveStatus = value !== '';

  const selectedStatus = EFAX_STATUS_FILTER_OPTIONS.find(
    (status) => status.value === value,
  );

  const handleSelectStatus = (status: EfaxStatusFilterValue) => {
    setOpen(false);
    onChange(status);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'border-input bg-background h-9 w-full justify-between rounded-md border px-3 font-normal',
            hasActiveStatus && 'border-blue-400 bg-blue-50',
          )}
          aria-label="Select status"
        >
          <span
            className={cn(
              'truncate text-sm',
              hasActiveStatus ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {selectedStatus?.label ?? 'Any Status'}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1"
        align="start"
      >
        {EFAX_STATUS_FILTER_OPTIONS.map((status) => {
          const isSelected = status.value === value;

          return (
            <Button
              key={status.value || 'any-status'}
              type="button"
              variant="ghost"
              className="h-auto w-full justify-start gap-2 px-2 py-1.5"
              onMouseDown={(event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                handleSelectStatus(status.value);
              }}
            >
              <span className="flex-1 text-left text-sm">{status.label}</span>
              {isSelected ? <Check className="size-4 shrink-0" /> : null}
            </Button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
